/**
 * Malzeme kataloğu ve stok ekranlarının metinleri (M12a; ADR 0026). Ekran adları tasarımdaki
 * gibi İngilizce (`satin-alma-akisi.jpeg`, `gemi-envanter-kumanya.jpeg`); Türkçe ikinci dil.
 */

const errorsEn = {
  GENERIC: 'The operation failed. Try again.',
  NETWORK: 'No connection. Check the network and try again.',
  FORBIDDEN: 'You are not allowed to do this.',
  NOT_FOUND: 'The record was not found. It may have been removed.',
  VALIDATION_FAILED: 'Some fields are invalid.',
  ROW_VERSION_CONFLICT: 'Someone else changed this record. Reopen it and try again.',
  DUPLICATE_MATERIAL: 'A material with the same name and specification already exists.',
  INVALID_MATERIAL: 'Some fields are invalid.',
  UNKNOWN_CATEGORY: 'The category no longer exists.',
  UNIT_IN_USE: 'The unit cannot change: the material is in stock on a vessel.',
  FILE_TOO_LARGE: 'The file is larger than 10 MB.',
  INVALID_FILE: 'The file could not be read. Upload an .xlsx file.',
  SHEET_MISSING: 'The file has no 01_ALL_ITEMS sheet.',
  INVALID_HEADER:
    'The column headers do not match: Main Category, Sub Category, Material / Item, Specification, UOM, Material Type.',
  DUPLICATE_STOCK_ITEM: 'This material is already in the vessel stock.',
  INVALID_STOCK_ITEM: 'Some fields are invalid.',
  UNKNOWN_MATERIAL: 'The material is no longer in the catalogue.',
  STOCK_ITEM_MATERIAL_FIXED: 'The material of a stock item cannot change.',
  INVALID_STOCK_MOVEMENT: 'Some fields are invalid.',
  NEGATIVE_STOCK: 'Not enough stock on hand.',
  FUTURE_DATE: 'The date cannot be in the future (ship time).',
  OFFICE_IS_WRITER: 'The catalogue is maintained by the office.',
  SHIP_IS_WRITER: 'Stock is recorded on board.',
};

const errorsTr: typeof errorsEn = {
  GENERIC: 'İşlem yapılamadı. Yeniden deneyin.',
  NETWORK: 'Bağlantı yok. Ağı denetleyip yeniden deneyin.',
  FORBIDDEN: 'Bu işlem için yetkiniz yok.',
  NOT_FOUND: 'Kayıt bulunamadı. Kaldırılmış olabilir.',
  VALIDATION_FAILED: 'Bazı alanlar geçersiz.',
  ROW_VERSION_CONFLICT: 'Kaydı başka biri değiştirdi. Yeniden açıp deneyin.',
  DUPLICATE_MATERIAL: 'Aynı ad ve özellikte bir malzeme zaten var.',
  INVALID_MATERIAL: 'Bazı alanlar geçersiz.',
  UNKNOWN_CATEGORY: 'Kategori artık yok.',
  UNIT_IN_USE: 'Birim değişemez: malzeme bir gemide stokta.',
  FILE_TOO_LARGE: 'Dosya 10 MB’tan büyük.',
  INVALID_FILE: 'Dosya okunamadı. .xlsx dosyası yükleyin.',
  SHEET_MISSING: 'Dosyada 01_ALL_ITEMS sayfası yok.',
  INVALID_HEADER:
    'Sütun başlıkları uymuyor: Main Category, Sub Category, Material / Item, Specification, UOM, Material Type.',
  DUPLICATE_STOCK_ITEM: 'Bu malzeme gemi stoğunda zaten var.',
  INVALID_STOCK_ITEM: 'Bazı alanlar geçersiz.',
  UNKNOWN_MATERIAL: 'Malzeme artık katalogda yok.',
  STOCK_ITEM_MATERIAL_FIXED: 'Stok kaleminin malzemesi değişemez.',
  INVALID_STOCK_MOVEMENT: 'Bazı alanlar geçersiz.',
  NEGATIVE_STOCK: 'Eldeki stok yetmiyor.',
  FUTURE_DATE: 'Tarih gelecekte olamaz (gemi saati).',
  OFFICE_IS_WRITER: 'Kataloğu ofis yönetir.',
  SHIP_IS_WRITER: 'Stok gemide kaydedilir.',
};

const page = {
  en: {
    first: 'First page',
    previous: 'Previous page',
    next: 'Next page',
    last: 'Last page',
    number: 'Page {{page}}',
  },
  tr: {
    first: 'İlk sayfa',
    previous: 'Önceki sayfa',
    next: 'Sonraki sayfa',
    last: 'Son sayfa',
    number: 'Sayfa {{page}}',
  },
};

export const materialsEn = {
  title: 'Material Master',
  description: 'Material catalogue: categories, units, codes and stock on board.',
  search: 'Search',
  searchPlaceholder: 'Search material, category, code…',
  filter: 'Filter',
  filters: 'Filters',
  category: 'Category',
  subCategory: 'Sub Category',
  materialType: 'Material Type',
  allCategories: 'All Categories',
  allSubCategories: 'All Sub Categories',
  allTypes: 'All Types',
  onlyStock: 'Only Stock Items',
  criticalOnly: 'Critical Items',
  reset: 'Reset',
  searchButton: 'Search',
  table: 'Materials',
  error: 'The material list could not be loaded.',
  retry: 'Try again',
  noMatch: 'No material matches the filters.',
  emptyTitle: 'The catalogue is empty.',
  emptyOffice: 'Import the Material Master Excel file or add materials one by one.',
  emptyShip: 'The catalogue arrives from the office with the next synchronisation.',
  columns: { material: 'Material', uom: 'UOM', type: 'Type' },
  showing: 'Showing {{from}}–{{to}} of {{total}}',
  pagination: 'Pages',
  page: page.en,
  edit: 'Edit',
  new: 'New Material',
  types: {
    Consumable: 'Consumable',
    'Spare Part': 'Spare Part',
    'Equipment/Spare': 'Equipment/Spare',
    // Kumanya kalemi (ADR 0099 §4)
    Provision: 'Provision',
  },
  detail: {
    title: 'Material Detail',
    error: 'The material could not be loaded.',
    noCode: 'No company code',
    criticalBadge: 'Critical',
    uom: 'UOM',
    specification: 'Specification',
    critical: 'Critical',
    yes: 'Yes',
    no: 'No',
    impaIssa: 'IMPA / ISSA',
    secondaryCategory: 'Secondary category',
    minStock: 'Min Stock',
    maxStock: 'Max Stock',
    onHand: 'On Hand',
    notStocked: 'Not kept on board',
    vesselStock: 'Stock on vessels',
    noVesselStock: 'No vessel keeps this material in stock.',
    vessel: 'Vessel',
    status: 'Status',
    relatedEquipment: 'Related Equipment',
    provision: 'Provision details',
    librarySource: 'Library',
    noEquipment: 'No related equipment',
    priceHistory: 'Price History',
    priceHistoryHint: 'Prices of issued purchase orders for this material, newest first.',
    noPrices: 'No purchase order for this material yet.',
    priceViews: 'Price history view',
    allOrders: 'All orders',
    byPort: 'By port',
    bySupplier: 'By supplier',
    orderDate: 'Order Date',
    poNo: 'PO No',
    supplier: 'Supplier',
    port: 'Port',
    qty: 'Qty',
    unitPrice: 'Unit Price',
    received: 'Received',
    lastPrice: 'Last Price',
    lowest: 'Lowest',
    orders: 'Orders',
    unknownPort: 'Port not given',
    showLatest: 'Show latest',
    viewAll: 'View all ({{count}})',
    notes: 'Notes',
    noNotes: 'No notes.',
  },
  form: {
    newTitle: 'New Material',
    editTitle: 'Edit Material',
    close: 'Close',
    category: 'Category',
    secondaryCategory: 'Second category',
    secondaryHint: 'The material is also listed under this category.',
    name: 'Material / Item',
    specification: 'Specification',
    unit: 'UOM',
    materialType: 'Material Type',
    companyCode: 'Company code',
    impaCode: 'IMPA code',
    issaCode: 'ISSA code',
    critical: 'Critical item',
    notes: 'Notes',
    cancel: 'Cancel',
    save: 'Save',
    errors: {
      required: 'Required.',
      tooLong: 'Too long.',
      sameCategory: 'The second category must differ from the category.',
      outOfRange: 'Out of range.',
      minAboveMax: 'The maximum must not be below the minimum.',
      zero: 'The quantity cannot be zero.',
      positive: 'Enter a positive quantity; the type gives the direction.',
      future: 'The date cannot be in the future.',
    },
  },
  import: {
    open: 'Import Excel',
    title: 'Import Material Master',
    close: 'Close',
    hint: 'Upload the Material Master Excel file (.xlsx, at most 10 MB). The 01_ALL_ITEMS sheet is read; a material with the same name and specification is updated, not duplicated.',
    file: 'Excel file',
    choose: 'Choose .xlsx file',
    working: 'Importing… a 5,000-item catalogue can take up to a minute.',
    cancel: 'Cancel',
    start: 'Import',
    done: 'Import finished: {{rows}} rows read.',
    summary: 'Import summary',
    counts: {
      added: 'Added',
      updated: 'Updated',
      unchanged: 'Unchanged',
      merged: 'Merged',
      skipped: 'Skipped',
      categoriesAdded: 'New categories',
    },
    issues: 'Rows to check',
    row: 'Row',
    field: 'Column',
    problem: 'Problem',
    crossTitle: 'Materials listed in more than one category',
    crossHint:
      'Each is kept as one material: the first category is primary, the next is the second category. Merging is the company’s decision.',
    item: 'Material',
    categories: 'Categories',
    rows: 'Rows',
    codes: {
      REQUIRED: 'Empty value',
      TOO_LONG: 'Text too long',
      INVALID_OPTION: 'Value not in the list',
      DUPLICATE_ROW: 'Repeated in the same category; skipped',
      EXTRA_CATEGORY: 'Third category not kept; the material is imported once',
      PROVISION_TYPE: 'Imported as Provision: the main category is Provisions',
      UNIT_IN_USE: 'Unit not changed: in stock on a vessel',
    },
    fields: {
      mainCategory: 'Main Category',
      subCategory: 'Sub Category',
      name: 'Material / Item',
      specification: 'Specification',
      unit: 'UOM',
      materialType: 'Material Type',
    },
  },
  errors: errorsEn,
};

export const materialsTr: typeof materialsEn = {
  title: 'Malzeme Kataloğu',
  description: 'Malzeme kataloğu: kategoriler, birimler, kodlar ve gemideki stok.',
  search: 'Arama',
  searchPlaceholder: 'Malzeme, kategori, kod ara…',
  filter: 'Süzgeç',
  filters: 'Süzgeçler',
  category: 'Kategori',
  subCategory: 'Alt kategori',
  materialType: 'Malzeme türü',
  allCategories: 'Bütün kategoriler',
  allSubCategories: 'Bütün alt kategoriler',
  allTypes: 'Bütün türler',
  onlyStock: 'Yalnız stoktakiler',
  criticalOnly: 'Kritik kalemler',
  reset: 'Sıfırla',
  searchButton: 'Ara',
  table: 'Malzemeler',
  error: 'Malzeme listesi yüklenemedi.',
  retry: 'Yeniden dene',
  noMatch: 'Süzgeçlere uyan malzeme yok.',
  emptyTitle: 'Katalog boş.',
  emptyOffice: 'Malzeme ana listesi Excel dosyasını içe aktarın ya da malzemeleri tek tek ekleyin.',
  emptyShip: 'Katalog bir sonraki senkronla ofisten iner.',
  columns: { material: 'Malzeme', uom: 'Birim', type: 'Tür' },
  showing: '{{total}} kayıttan {{from}}–{{to}}',
  pagination: 'Sayfalar',
  page: page.tr,
  edit: 'Düzelt',
  new: 'Yeni malzeme',
  types: {
    Consumable: 'Sarf',
    'Spare Part': 'Yedek parça',
    'Equipment/Spare': 'Ekipman/Yedek',
    Provision: 'Kumanya',
  },
  detail: {
    title: 'Malzeme kartı',
    error: 'Malzeme yüklenemedi.',
    noCode: 'Şirket kodu yok',
    criticalBadge: 'Kritik',
    uom: 'Birim',
    specification: 'Özellik',
    critical: 'Kritik',
    yes: 'Evet',
    no: 'Hayır',
    impaIssa: 'IMPA / ISSA',
    secondaryCategory: 'İkinci kategori',
    minStock: 'En az stok',
    maxStock: 'En çok stok',
    onHand: 'Eldeki',
    notStocked: 'Gemide tutulmuyor',
    vesselStock: 'Gemilerdeki stok',
    noVesselStock: 'Bu malzemeyi stokta tutan gemi yok.',
    vessel: 'Gemi',
    status: 'Durum',
    relatedEquipment: 'İlgili ekipman',
    provision: 'Kumanya bilgileri',
    librarySource: 'Kütüphane',
    noEquipment: 'İlgili ekipman yok',
    priceHistory: 'Fiyat geçmişi',
    priceHistoryHint: 'Bu malzemenin verilmiş siparişlerdeki fiyatları, en yeni önce.',
    noPrices: 'Bu malzeme için henüz sipariş yok.',
    priceViews: 'Fiyat geçmişi görünümü',
    allOrders: 'Bütün siparişler',
    byPort: 'Limana göre',
    bySupplier: 'Tedarikçiye göre',
    orderDate: 'Sipariş tarihi',
    poNo: 'PO no',
    supplier: 'Tedarikçi',
    port: 'Liman',
    qty: 'Miktar',
    unitPrice: 'Birim fiyat',
    received: 'Teslim alınan',
    lastPrice: 'Son fiyat',
    lowest: 'En düşük',
    orders: 'Sipariş',
    unknownPort: 'Liman girilmemiş',
    showLatest: 'Son kayıtlar',
    viewAll: 'Tümü ({{count}})',
    notes: 'Notlar',
    noNotes: 'Not yok.',
  },
  form: {
    newTitle: 'Yeni malzeme',
    editTitle: 'Malzemeyi düzelt',
    close: 'Kapat',
    category: 'Kategori',
    secondaryCategory: 'İkinci kategori',
    secondaryHint: 'Malzeme bu kategoride de listelenir.',
    name: 'Malzeme / kalem',
    specification: 'Özellik',
    unit: 'Birim',
    materialType: 'Malzeme türü',
    companyCode: 'Şirket kodu',
    impaCode: 'IMPA kodu',
    issaCode: 'ISSA kodu',
    critical: 'Kritik kalem',
    notes: 'Notlar',
    cancel: 'Vazgeç',
    save: 'Kaydet',
    errors: {
      required: 'Zorunlu.',
      tooLong: 'Çok uzun.',
      sameCategory: 'İkinci kategori kategoriden farklı olmalı.',
      outOfRange: 'Aralık dışında.',
      minAboveMax: 'En çok, en azdan küçük olamaz.',
      zero: 'Miktar sıfır olamaz.',
      positive: 'Artı miktar girin; yönü tür belirler.',
      future: 'Tarih gelecekte olamaz.',
    },
  },
  import: {
    open: 'Excel’den içe aktar',
    title: 'Malzeme ana listesini içe aktar',
    close: 'Kapat',
    hint: 'Malzeme ana listesi Excel dosyasını yükleyin (.xlsx, en çok 10 MB). 01_ALL_ITEMS sayfası okunur; aynı ad ve özellikteki malzeme güncellenir, yeniden açılmaz.',
    file: 'Excel dosyası',
    choose: '.xlsx dosyası seçin',
    working: 'İçe aktarılıyor… 5.000 kalemlik katalog bir dakika sürebilir.',
    cancel: 'Vazgeç',
    start: 'İçe aktar',
    done: 'İçe aktarma bitti: {{rows}} satır okundu.',
    summary: 'İçe aktarma özeti',
    counts: {
      added: 'Eklenen',
      updated: 'Güncellenen',
      unchanged: 'Değişmeyen',
      merged: 'Birleşen',
      skipped: 'Atlanan',
      categoriesAdded: 'Yeni kategori',
    },
    issues: 'Denetlenecek satırlar',
    row: 'Satır',
    field: 'Sütun',
    problem: 'Sorun',
    crossTitle: 'Birden çok kategoride geçen malzemeler',
    crossHint:
      'Her biri tek malzeme olarak tutulur: ilk kategori birincil, sonraki ikinci kategoridir. Birleştirme kararı şirketindir.',
    item: 'Malzeme',
    categories: 'Kategoriler',
    rows: 'Satırlar',
    codes: {
      REQUIRED: 'Boş değer',
      TOO_LONG: 'Metin çok uzun',
      INVALID_OPTION: 'Değer listede yok',
      DUPLICATE_ROW: 'Aynı kategoride tekrar; atlandı',
      EXTRA_CATEGORY: 'Üçüncü kategori saklanmadı; malzeme bir kez alındı',
      PROVISION_TYPE: 'Kumanya (Provision) olarak alındı: ana kategori Provisions',
      UNIT_IN_USE: 'Birim değişmedi: bir gemide stokta',
    },
    fields: {
      mainCategory: 'Ana kategori',
      subCategory: 'Alt kategori',
      name: 'Malzeme / kalem',
      specification: 'Özellik',
      unit: 'Birim',
      materialType: 'Malzeme türü',
    },
  },
  errors: errorsTr,
};

export const stockEn = {
  vessel: 'Vessel',
  category: 'Category',
  allCategories: 'All Categories',
  all: 'All',
  material: 'Material',
  searchMaterial: 'Search material…',
  search: 'Search',
  filters: 'Filters',
  error: 'The stock could not be loaded.',
  retry: 'Try again',
  emptyTitle: 'No stock items yet.',
  emptyShip: 'Add the materials kept on board with their minimum and maximum levels.',
  emptyWeb: 'The vessel has not recorded stock yet; it arrives with synchronisation.',
  noMatch: 'No stock item matches the filters.',
  showing: 'Showing {{from}}–{{to}} of {{total}}',
  pagination: 'Pages',
  pageSize: 'Rows per page',
  perPage: '{{size}} / page',
  loading: 'Loading…',
  historyOf: 'Movements of {{name}}',
  editLevels: 'Edit levels of {{name}}',
  sortBy: 'Sort by {{column}}',
  management: {
    title: 'Stock Management',
    descriptionShip: 'Stock on hand with minimum and maximum levels.',
    descriptionWeb: 'Stock on hand of each vessel (read-only; recorded on board).',
    add: 'Add Stock Item',
    table: 'Stock items',
  },
  columns: {
    material: 'Material',
    category: 'Category',
    subCategory: 'Sub Category',
    uom: 'UOM',
    onHand: 'On Hand',
    minStock: 'Min Stock',
    maxStock: 'Max Stock',
    lastPrice: 'Last Price',
    value: 'Value',
    status: 'Status',
    actions: 'Actions',
    itemName: 'Item Name',
    issa: 'ISSA Code',
    impa: 'IMPA Code',
    unit: 'Unit',
    location: 'Location',
    minLevel: 'Min. Level',
    date: 'Date',
    type: 'Type',
    qty: 'Qty',
    user: 'User',
    reason: 'Reason',
    item: 'Item',
    number: 'No.',
  },
  status: {
    management: { normal: 'OK', low: 'Low Stock', critical: 'Critical' },
    inventory: { normal: 'Normal', low: 'Low', critical: 'Critical' },
  },
  kinds: {
    in: 'Stock In',
    out: 'Stock Out',
    adjust: 'Adjustment',
    receipt: 'Goods Receipt',
    transfer: 'Transfer',
  },
  itemForm: {
    newTitle: 'Add Stock Item',
    editTitle: 'Edit Stock Levels',
    close: 'Close',
    material: 'Material',
    materialHint: 'Type to search the catalogue; materials already in stock are disabled.',
    materialFixed: 'The material of a stock item does not change.',
    noMaterial: 'No material found',
    location: 'Location',
    minQty: 'Min Stock',
    maxQty: 'Max Stock',
    reorderLevel: 'Reorder Level',
    openingQty: 'Quantity on Hand',
    openingQtyHint:
      'Optional. Recorded as an opening stock entry for today; leave empty to add the item only.',
    cancel: 'Cancel',
    save: 'Save',
  },
  movementForm: {
    title: { in: 'Add Stock In', out: 'Add Stock Out', adjust: 'Stock Adjustment' },
    close: 'Close',
    kind: 'Movement type',
    kinds: { in: 'Stock In', out: 'Stock Out', adjust: 'Adjustment' },
    item: 'Item',
    noItem: 'No item found',
    onHand: 'On hand {{onHand}} · {{location}}',
    onHandAfter: 'On hand {{onHand}} → {{after}} after this entry · {{location}}',
    quantity: 'Quantity',
    signedQuantity: 'Quantity (+ / −)',
    date: 'Date',
    dateHint: 'Ship date (today {{today}})',
    reason: 'Note',
    reasonRequired: 'Reason (required for an adjustment)',
    immutable:
      'A movement cannot be edited later; a wrong entry is corrected with a reverse movement.',
    cancel: 'Cancel',
    save: { in: 'Save Stock In', out: 'Save Stock Out', adjust: 'Save Adjustment' },
    negative: 'Not enough stock: {{onHand}} {{unit}} on hand.',
  },
  history: {
    title: 'Movements · {{name}}',
    close: 'Close',
    error: 'The movements could not be loaded.',
    empty: 'No movements yet.',
    date: 'Date',
    type: 'Type',
    quantity: 'Quantity',
    user: 'User',
    reason: 'Reason',
  },
  inOut: {
    descriptionShip: 'Stock in and out on board, inventory levels and alerts.',
    descriptionWeb: 'Vessel stock, movements and alerts (read-only; recorded on board).',
    head: 'Inventory',
    headTitle: 'Inventory & Provision',
    sections: 'Inventory sections',
    purchasingIntegration: 'Purchasing Integration',
    imo: 'IMO',
    locations: 'Locations',
    date: 'Date',
    searchPlaceholder: 'Search item, ISSA, IMPA, description…',
    newRecord: 'New Record',
    summary: 'Inventory summary',
    total: 'Total Inventory Items',
    low: 'Low Stock Items',
    critical: 'Critical Items',
    todayIn: 'Today Stock In',
    todayOut: 'Today Stock Out',
    distribution: 'Stock Level Distribution',
    tree: 'Inventory Tree',
    treeSearch: 'Search in inventory…',
    allInventory: 'All Inventory',
    expand: 'Expand {{name}}',
    collapse: 'Collapse {{name}}',
    list: 'Stock In / Out - Inventory List',
    tabs: { all: 'All Items', in: 'Stock In', out: 'Stock Out', history: 'History' },
    code: 'ISSA/IMPA Code',
    anyCode: 'Any code',
    location: 'Location',
    item: 'Item',
    searchItem: 'Search item…',
    showCategory: 'Show {{name}} items',
    noMovements: 'No movements yet.',
    stockInFor: 'Stock in: {{name}}',
    stockOutFor: 'Stock out: {{name}}',
    stockSummary: 'Stock Summary',
    refresh: 'Refresh stock',
    byCategory: 'By Category',
    byLocation: 'By Location',
    noSummary: 'No stock items yet.',
    items: 'Items',
    noLocation: 'No location',
    totalRow: 'Total',
    recent: 'Recent Transactions',
    recentTable: 'Recent stock movements',
    alerts: 'Alerts & Notifications',
    viewAll: 'View All',
    noAlerts: 'All items are above their minimum level.',
    alert: {
      lowBelow: '{{name}} stock is below minimum level ({{quantity}})',
      lowAt: '{{name}} stock is at minimum level ({{quantity}})',
      critical: '{{name}} stock is critical ({{quantity}})',
    },
    alertDetail: 'on hand {{onHand}}, minimum {{min}}',
    quickActions: 'Quick Actions',
    addIn: 'Add Stock In',
    addOut: 'Add Stock Out',
    provisionRequest: 'Create Provision Request',
    purchasingRequests: 'View Purchasing Requests',
    readOnly: 'Stock is recorded on board; the office sees it after synchronisation.',
    saved: {
      in: 'Stock in recorded: {{quantity}} {{unit}} {{name}}.',
      out: 'Stock out recorded: {{quantity}} {{unit}} {{name}}.',
      adjust: 'Adjustment recorded: {{quantity}} {{unit}} {{name}}.',
    },
  },
};

export const stockTr: typeof stockEn = {
  vessel: 'Gemi',
  category: 'Kategori',
  allCategories: 'Bütün kategoriler',
  all: 'Tümü',
  material: 'Malzeme',
  searchMaterial: 'Malzeme ara…',
  search: 'Ara',
  filters: 'Süzgeçler',
  error: 'Stok yüklenemedi.',
  retry: 'Yeniden dene',
  emptyTitle: 'Henüz stok kalemi yok.',
  emptyShip: 'Gemide tutulan malzemeleri en az ve en çok seviyeleriyle ekleyin.',
  emptyWeb: 'Gemi henüz stok kaydetmedi; stok senkronla gelir.',
  noMatch: 'Süzgeçlere uyan stok kalemi yok.',
  showing: '{{total}} kayıttan {{from}}–{{to}}',
  pagination: 'Sayfalar',
  pageSize: 'Sayfa başına satır',
  perPage: '{{size}} / sayfa',
  loading: 'Yükleniyor…',
  historyOf: '{{name}} hareketleri',
  editLevels: '{{name}} seviyelerini düzelt',
  sortBy: '{{column}} sütununa göre sırala',
  management: {
    title: 'Stok Yönetimi',
    descriptionShip: 'Eldeki stok, en az ve en çok seviyeleriyle.',
    descriptionWeb: 'Her geminin eldeki stoğu (salt okunur; gemide kaydedilir).',
    add: 'Stok kalemi ekle',
    table: 'Stok kalemleri',
  },
  columns: {
    material: 'Malzeme',
    category: 'Kategori',
    subCategory: 'Alt kategori',
    uom: 'Birim',
    onHand: 'Eldeki',
    minStock: 'En az',
    maxStock: 'En çok',
    lastPrice: 'Son fiyat',
    value: 'Değer',
    status: 'Durum',
    actions: 'İşlemler',
    itemName: 'Kalem adı',
    issa: 'ISSA kodu',
    impa: 'IMPA kodu',
    unit: 'Birim',
    location: 'Yer',
    minLevel: 'En az seviye',
    date: 'Tarih',
    type: 'Tür',
    qty: 'Miktar',
    user: 'Kullanıcı',
    reason: 'Gerekçe',
    item: 'Kalem',
    number: 'Sıra',
  },
  status: {
    management: { normal: 'Yeterli', low: 'Düşük stok', critical: 'Kritik' },
    inventory: { normal: 'Normal', low: 'Düşük', critical: 'Kritik' },
  },
  kinds: {
    in: 'Stok girişi',
    out: 'Stok çıkışı',
    adjust: 'Düzeltme',
    receipt: 'Mal kabulü',
    transfer: 'Transfer',
  },
  itemForm: {
    newTitle: 'Stok kalemi ekle',
    editTitle: 'Stok seviyelerini düzelt',
    close: 'Kapat',
    material: 'Malzeme',
    materialHint: 'Katalogda aramak için yazın; stokta olan malzemeler seçilemez.',
    materialFixed: 'Stok kaleminin malzemesi değişmez.',
    noMaterial: 'Malzeme bulunamadı',
    location: 'Yer',
    minQty: 'En az stok',
    maxQty: 'En çok stok',
    reorderLevel: 'Yeniden sipariş seviyesi',
    openingQty: 'Eldeki miktar',
    openingQtyHint:
      'İsteğe bağlı. Bugünün tarihiyle açılış girişi olarak kaydedilir; boş bırakılırsa yalnız kalem eklenir.',
    cancel: 'Vazgeç',
    save: 'Kaydet',
  },
  movementForm: {
    title: { in: 'Stok girişi ekle', out: 'Stok çıkışı ekle', adjust: 'Stok düzeltmesi' },
    close: 'Kapat',
    kind: 'Hareket türü',
    kinds: { in: 'Giriş', out: 'Çıkış', adjust: 'Düzeltme' },
    item: 'Kalem',
    noItem: 'Kalem bulunamadı',
    onHand: 'Eldeki {{onHand}} · {{location}}',
    onHandAfter: 'Eldeki {{onHand}} → bu kayıttan sonra {{after}} · {{location}}',
    quantity: 'Miktar',
    signedQuantity: 'Miktar (+ / −)',
    date: 'Tarih',
    dateHint: 'Gemi tarihi (bugün {{today}})',
    reason: 'Not',
    reasonRequired: 'Gerekçe (düzeltmede zorunlu)',
    immutable: 'Hareket sonradan değiştirilemez; yanlış giriş ters hareketle düzeltilir.',
    cancel: 'Vazgeç',
    save: { in: 'Girişi kaydet', out: 'Çıkışı kaydet', adjust: 'Düzeltmeyi kaydet' },
    negative: 'Eldeki stok yetmiyor: {{onHand}} {{unit}} var.',
  },
  history: {
    title: 'Hareketler · {{name}}',
    close: 'Kapat',
    error: 'Hareketler yüklenemedi.',
    empty: 'Henüz hareket yok.',
    date: 'Tarih',
    type: 'Tür',
    quantity: 'Miktar',
    user: 'Kullanıcı',
    reason: 'Gerekçe',
  },
  inOut: {
    descriptionShip: 'Gemide stok giriş ve çıkışı, envanter seviyeleri ve uyarılar.',
    descriptionWeb: 'Gemi stoğu, hareketler ve uyarılar (salt okunur; gemide kaydedilir).',
    head: 'Envanter',
    headTitle: 'Envanter ve Kumanya',
    sections: 'Envanter bölümleri',
    purchasingIntegration: 'Satın alma bağlantısı',
    imo: 'IMO',
    locations: 'Yerler',
    date: 'Tarih',
    searchPlaceholder: 'Kalem, ISSA, IMPA, açıklama ara…',
    newRecord: 'Yeni kayıt',
    summary: 'Envanter özeti',
    total: 'Toplam envanter kalemi',
    low: 'Düşük stoklu kalem',
    critical: 'Kritik kalem',
    todayIn: 'Bugünkü giriş',
    todayOut: 'Bugünkü çıkış',
    distribution: 'Stok seviye dağılımı',
    tree: 'Envanter ağacı',
    treeSearch: 'Envanterde ara…',
    allInventory: 'Bütün envanter',
    expand: '{{name}} aç',
    collapse: '{{name}} kapat',
    list: 'Stok giriş / çıkış - envanter listesi',
    tabs: { all: 'Bütün kalemler', in: 'Giriş', out: 'Çıkış', history: 'Geçmiş' },
    code: 'ISSA/IMPA kodu',
    anyCode: 'Bütün kodlar',
    location: 'Yer',
    item: 'Kalem',
    searchItem: 'Kalem ara…',
    showCategory: '{{name}} kalemlerini göster',
    noMovements: 'Henüz hareket yok.',
    stockInFor: 'Stok girişi: {{name}}',
    stockOutFor: 'Stok çıkışı: {{name}}',
    stockSummary: 'Stok özeti',
    refresh: 'Stoğu yenile',
    byCategory: 'Kategoriye göre',
    byLocation: 'Yere göre',
    noSummary: 'Henüz stok kalemi yok.',
    items: 'Kalem',
    noLocation: 'Yeri yok',
    totalRow: 'Toplam',
    recent: 'Son hareketler',
    recentTable: 'Son stok hareketleri',
    alerts: 'Uyarılar ve bildirimler',
    viewAll: 'Tümünü gör',
    noAlerts: 'Bütün kalemler en az seviyenin üstünde.',
    alert: {
      lowBelow: '{{name}} stoğu en az seviyenin altında ({{quantity}})',
      lowAt: '{{name}} stoğu en az seviyede ({{quantity}})',
      critical: '{{name}} stoğu kritik ({{quantity}})',
    },
    alertDetail: 'eldeki {{onHand}}, en az {{min}}',
    quickActions: 'Hızlı işlemler',
    addIn: 'Stok girişi ekle',
    addOut: 'Stok çıkışı ekle',
    provisionRequest: 'Kumanya talebi oluştur',
    purchasingRequests: 'Satın alma taleplerini gör',
    readOnly: 'Stok gemide kaydedilir; ofis senkrondan sonra görür.',
    saved: {
      in: 'Stok girişi kaydedildi: {{quantity}} {{unit}} {{name}}.',
      out: 'Stok çıkışı kaydedildi: {{quantity}} {{unit}} {{name}}.',
      adjust: 'Düzeltme kaydedildi: {{quantity}} {{unit}} {{name}}.',
    },
  },
};
